import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { MastermindCirclesContent } from "./components/contents/mastermindCircles/MastermindCirclesContent";
import { ExperiencesContent } from "./components/contents/experiences/ExperiencesContent";
import { IntroductionContent } from "./components/contents/introductions/IntroductionContent";
import { ExpertiseContent } from "./components/contents/expertise/ExpertiseContent";
import { MessageItem } from "./components/MessageItem";
import { MessagesListHeader } from "./components/MessagesListHeader";
import { MessageSimpleModal } from "./components/modals/MessageSimpleModal";
import { MessageCancelationPolicy } from "./components/modals/MessageCancelationPolicy";
import { CommentsMessagesModel } from "./models/CommentsMessagesModel";
import * as chatService from "../../services/chatService";
import { useHistory, useLocation } from "react-router-dom";
import { useConfirmAction } from "../../hooks/utils/useConfirmAction";

const MessagesPage = () => {
    const history = useHistory();
    const location = useLocation();
    const currentUser = useSelector((state: any) => state.auth?.user);
    const authToken = useSelector((state: any) => state.auth?.accessToken);

    const [conversations, setConversations] = useState<any[]>([]);
    const [filteredConversations, setFilteredConversations] = useState<any[]>([]);
    const [selected, setSelected] = useState<any | null>(null);
    const [messages, setMessages] = useState<CommentsMessagesModel[]>([]);
    const [sharedKey, setSharedKey] = useState<CryptoKey | null>(null);
    const [keysReady, setKeysReady] = useState<boolean>(false);
    const [nextPage, setNextPage] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);

    // Deep linking from query params
    const searchParams = new URLSearchParams(location.search);
    const targetUuid = searchParams.get("uuid");

    // Filters
    const [search, setSearch] = useState<string>("");
    const [filter, setFilter] = useState<string>("all");

    // Modal control
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalAction, setModalAction] = useState<"leave" | "archive" | null>(null);

    const activeSocketRef = useRef<WebSocket | null>(null);
    const notificationSocketRef = useRef<WebSocket | null>(null);

    useEffect(() => {
        document.body.classList.add('community-wrapper')
        return () => document.body.classList.remove('community-wrapper')
    }, []);

    // 1. Initialize E2EE Keys
    useEffect(() => {
        if (currentUser) {
            chatService.generateAndRegisterKeyPair(currentUser.id).then(() => {
                setKeysReady(true);
            }).catch(err => {
                console.error("Failed to generate/register E2EE keys:", err);
                setKeysReady(true); // Fallback to let the app load even if key generation fails
            });
        }
    }, [currentUser]);

    const [convLimit] = useState(1000);
    const [hasMoreConversations] = useState(false);
    const [loadingConversations, setLoadingConversations] = useState(false);

    // 2. Fetch Conversations
    const loadConversations = useCallback(async (isLoadMore = false) => {
        if (!keysReady || !currentUser || loadingConversations) return;
        setLoadingConversations(true);
        const data = await chatService.getConversations({ limit: convLimit, offset: 0 });

        // Decrypt last message for private E2EE chats (introductions & experiences)
        await Promise.all(data.map(async (c: any) => {
            const peerKey = c.peerPublicKey || c.peer_public_key;
            const lastMsg = c.lastMessage || c.last_message;
            if (peerKey && lastMsg && lastMsg.ciphertext && lastMsg.nonce && lastMsg.tag) {
                try {
                    const sharedKey = await chatService.getSharedSecretKey(peerKey, currentUser.id);
                    const decrypted = await chatService.decryptMessage(lastMsg.ciphertext, lastMsg.nonce, lastMsg.tag, sharedKey);
                    if (c.lastMessage) {
                        c.lastMessage.previewText = decrypted;
                        c.lastMessage.preview_text = decrypted;
                    }
                    if (c.last_message) {
                        c.last_message.preview_text = decrypted;
                        c.last_message.previewText = decrypted;
                    }
                } catch (e) {
                    console.error("Failed to decrypt last message preview:", e);
                }
            }
        }));

        setConversations(data);
        setLoadingConversations(false);
    }, [keysReady, currentUser, convLimit, loadingConversations]);

    useEffect(() => {
        if (keysReady) {
            loadConversations(false);
        }
    }, [keysReady]);

    // Auto-select conversation by targetUuid from URL.
    //
    // Se recuerda que uuid ya se consumio (autoSelectedUuidRef) para NO reimponerlo cuando el
    // usuario cambia manualmente de hilo. Antes esta ref no existia y el efecto reaccionaba a
    // cambios en `selected` (una recarga silenciosa incluida): reencontraba la conversation del
    // query string y devolvia la seleccion al hilo original, dejando la pantalla "pegada" a la
    // conversation que abrio la card de /my-bookings o /my-expertise.
    const autoSelectedUuidRef = useRef<string | null>(null);
    useEffect(() => {
        if (!targetUuid) return;
        if (autoSelectedUuidRef.current === targetUuid) return;
        if (conversations.length === 0) return;

        const found = conversations.find(c => c.uuid === targetUuid);
        if (found) {
            autoSelectedUuidRef.current = targetUuid;
            setSelected(found);
        } else {
            chatService.getConversation(targetUuid).then((conv) => {
                if (conv && conv.uuid) {
                    autoSelectedUuidRef.current = conv.uuid;
                    setConversations(prev => [conv, ...prev.filter(c => c.uuid !== conv.uuid)]);
                    setSelected(conv);
                }
            }).catch(err => {
                console.error("Failed to load conversation from uuid:", err);
            });
        }
    }, [conversations, targetUuid]);

    // Sync manual selection back into the URL.
    //
    // El click en la lista solo cambiaba el state local; el query `?uuid=` se quedaba con el
    // valor que trajo el link de la card. Como el efecto de arriba lee ese query, si se le
    // dejaba pasar podia reponer la seleccion vieja. Al reescribir la URL con la conversation
    // recien elegida cerramos ese ciclo y ademas el URL vuelve a ser deep-linkable.
    const handleSelectConversation = useCallback((conv: any) => {
        if (!conv?.uuid) return;
        setSelected(conv);
        autoSelectedUuidRef.current = conv.uuid;
        const params = new URLSearchParams(location.search);
        if (params.get('uuid') !== conv.uuid) {
            params.set('uuid', conv.uuid);
            history.replace({ pathname: location.pathname, search: params.toString() });
        }
    }, [history, location.pathname, location.search]);

    // 3. Filter Conversations
    useEffect(() => {
        let list = Array.isArray(conversations) ? conversations : [];
        if (filter !== "all") {
            list = list.filter(c => {
                const cType = c.chatType ?? c.chat_type;
                if (filter === "forum") {
                    return cType === "forum" || cType === "circle";
                }
                return cType === filter;
            });
        }
        if (search.trim()) {
            const query = search.toLowerCase();
            list = list.filter(c => c.title?.toLowerCase().includes(query));
        }
        setFilteredConversations(list);
    }, [conversations, filter, search]);

    // Helper to get WS base URL
    const getWsUrl = useCallback(() => {
        const apiUrl = process.env.REACT_APP_API_URL || "https://api.joinnobilis.com/api/v1";
        const wsBase = apiUrl.replace(/^http/, "ws").replace(/\/api\/v1\/?$/, "");
        return wsBase;
    }, []);

    // 4. Listen to Global Notifications/Left Panel updates
    useEffect(() => {
        if (!authToken) return;

        const wsBase = getWsUrl();
        const wsUrl = `${wsBase}/api/v1/ws/notifications/?token=${authToken}`;
        const ws = new WebSocket(wsUrl);
        notificationSocketRef.current = ws;

        ws.onmessage = (event) => {
            try {
                const payload = JSON.parse(event.data);
                if (payload.stream === "left_panel") {
                    loadConversations();
                }
            } catch (err) {
                // Ignore parsing errors
            }
        };

        return () => {
            ws.close();
        };
    }, [authToken, getWsUrl, loadConversations]);

    const mapMessage = useCallback(async (msg: any, derivedKey: CryptoKey | null): Promise<CommentsMessagesModel> => {
        let content = msg.cleartextContent || msg.cleartext_content || msg.message || "";
        const chatType = selected?.chatType || selected?.chat_type || "";
        const isPublic = msg.isPublic ?? msg.is_public ?? selected?.isPublic ?? selected?.is_public ?? (chatType === "forum" || chatType === "circle");

        if (!isPublic && msg.ciphertext && msg.nonce && msg.tag && derivedKey) {
            content = await chatService.decryptMessage(msg.ciphertext, msg.nonce, msg.tag, derivedKey);
        } else if (msg.ciphertext) {
            content = "[Mensaje Cifrado]";
        }

        const currentUserId = currentUser?.id || 0;
        const replyDetail = msg.replyToDetail || msg.reply_to_detail;

        let replyDetailMapped: any = undefined;
        if (replyDetail) {
            let replyContent = replyDetail.message || "";
            if (!isPublic && replyDetail.ciphertext && replyDetail.nonce && replyDetail.tag && derivedKey) {
                try {
                    replyContent = await chatService.decryptMessage(replyDetail.ciphertext, replyDetail.nonce, replyDetail.tag, derivedKey);
                } catch (e) {
                    replyContent = "[Mensaje cifrado - Clave incorrecta]";
                }
            } else if (replyDetail.ciphertext) {
                replyContent = "[Mensaje Cifrado]";
            }

            replyDetailMapped = {
                id: String(msg.replyTo ?? msg.reply_to),
                avatar: "",
                author: replyDetail.senderName || replyDetail.sender_name,
                isMine: false,
                content: replyContent
            };
        }

        const isMine = msg.sender?.id && currentUser?.id ? Number(msg.sender.id) === Number(currentUser.id) : false;
        const authorName = isMine ? "You" : (msg.sender?.name || "User");

        return {
            id: msg.id,
            avatar: msg.sender?.avatar || "",
            author: authorName,
            authorId: msg.sender?.id || 0,
            content: content,
            reactions: msg.reactions?.length || 0,
            isLiked: msg.reactions?.some((r: any) => r.user?.id === currentUserId) || false,
            reply: replyDetailMapped,
            files: msg.resources?.map((r: any) => ({
                id: String(r.id),
                file: r.file,
                resourceType: r.fileType ?? r.file_type ?? "document",
                title: r.title,
                size: r.sizeBytes || r.size_bytes || r.size || "N/A",
                sizeBytes: r.sizeBytes || r.size_bytes || r.size || "N/A",
                previewImage: r.file
            })) || [],
            createdAt: msg.createdAt || msg.created_at || msg.stamptime || new Date().toISOString(),
            isMine: isMine,
            // Los hilos de Expertise traen mensajes de SISTEMA (solicitud, reagenda, confirmacion)
            // que se pintan como tarjeta y no como burbuja. Sin propagar el tipo aqui llegan como
            // undefined y el contenido no encuentra ninguna.
            messageType: msg.messageType ?? msg.message_type ?? 'user'
        };
    }, [currentUser, selected]);

    // 6. Handle Conversation Selection & WS Chat Channel Connection
    useEffect(() => {
        if (!selected || !keysReady) {
            setMessages([]);
            setSharedKey(null);
            return;
        }

        let isMounted = true;

        const initChat = async () => {
            let localSharedKey: CryptoKey | null = null;
            const peerKey = selected.peerPublicKey || selected.peer_public_key;
            if (peerKey) {
                try {
                    localSharedKey = await chatService.getSharedSecretKey(peerKey, currentUser.id);
                    setSharedKey(localSharedKey);
                } catch (e) {
                    console.error("E2EE error:", e);
                }
            }

            // Load message history
            const response = await chatService.getMessages(selected.uuid);
            const mappedHistory = await Promise.all(response.results.map((m: any) => mapMessage(m, localSharedKey)));
            if (isMounted) {
                setMessages(mappedHistory);
                setNextPage(response.next);
                
                // Reset local unread_count and call backend mark-as-read
                setConversations(prev => prev.map(c => {
                    if (c.uuid === selected.uuid) {
                        return { ...c, unreadCount: 0, unread_count: 0 };
                    }
                    return c;
                }));
                chatService.markConversationAsRead(selected.uuid);
            }

            // Connect dedicated Chat WebSocket
            if (!authToken) return;
            const wsBase = getWsUrl();
            const wsUrl = `${wsBase}/api/v1/ws/chat/${selected.uuid}/?token=${authToken}`;
            const ws = new WebSocket(wsUrl);
            activeSocketRef.current = ws;

            ws.onmessage = async (event) => {
                try {
                    const payload = JSON.parse(event.data);
                    if (payload.stream === "central_panel") {
                        if (payload.event === "message_created") {
                            const newMsg = await mapMessage(payload.data, localSharedKey || sharedKey);
                            if (isMounted) setMessages(prev => [newMsg, ...prev]);
                        } else if (payload.event === "message_updated" || payload.event === "reaction_added" || payload.event === "reaction_removed") {
                            const updatedMsg = await mapMessage(payload.data, localSharedKey || sharedKey);
                            if (isMounted) setMessages(prev => prev.map(m => m.id === updatedMsg.id ? updatedMsg : m));
                        } else if (payload.event === "message_deleted") {
                            if (isMounted) setMessages(prev => prev.filter(m => m.id !== payload.data.id));
                        }
                    }
                } catch (err) {
                    // Ignore parser errors
                }
            };
        };

        initChat();

        return () => {
            isMounted = false;
            if (activeSocketRef.current) {
                activeSocketRef.current.close();
            }
        };
    }, [selected, keysReady, authToken, getWsUrl, mapMessage, currentUser]);

    // 7. Operations: Send/Edit Message
    const handleSendMessage = async (text: string, files?: File[], replyToId?: number, editingMessageId?: number) => {
        if (!selected) return;

        const chatType = selected.chatType || selected.chat_type;

        if (editingMessageId) {
            // Edit Mode
            if (chatType === "forum" || chatType === "circle") {
                await chatService.updateCommunityComment(editingMessageId, text);
            } else {
                let payload: any = {};
                if (sharedKey) {
                    const encrypted = await chatService.encryptMessage(text, sharedKey);
                    payload = {
                        ciphertext: encrypted.ciphertext,
                        nonce: encrypted.nonce,
                        tag: encrypted.tag
                    };
                } else {
                    payload = { cleartextContent: text };
                }
                await chatService.updateMessage(editingMessageId, payload);
            }
            return;
        }

        // Extract URLs dynamically from text
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const links: string[] = [];
        let match;
        while ((match = urlRegex.exec(text)) !== null) {
            links.push(match[0]);
        }

        let payload: any = {};
        if (chatType === "introduction" || chatType === "experience") {
            if (sharedKey) {
                const encrypted = await chatService.encryptMessage(text, sharedKey);
                payload = {
                    ciphertext: encrypted.ciphertext,
                    nonce: encrypted.nonce,
                    tag: encrypted.tag
                };
            } else {
                payload = { cleartextContent: text };
            }
        } else {
            payload = { cleartextContent: text };
        }

        if (replyToId) {
            payload.replyTo = replyToId;
        }

        if (links.length > 0) {
            payload.links = links;
        }

        await chatService.sendMessage(selected.uuid, payload, files);
    };

    // 8. Operations: Reaction
    const handleReact = async (messageId: number, emoji: string) => {
        await chatService.reactToMessage(messageId, emoji);
    };

    // Hook Confirm Dialog
    const confirmAction = useConfirmAction();

    // 8.5 Operations: Delete Message
    const handleDeleteMessage = async (msg: CommentsMessagesModel) => {
        const confirmed = await confirmAction({
            title: "Eliminar mensaje",
            message: "¿Estás seguro de que deseas eliminar este mensaje? Esta acción no se puede deshacer.",
            btnTextAction: "Eliminar",
            btnTextDecline: "Cancelar"
        });

        if (confirmed) {
            const chatType = selected?.chatType || selected?.chat_type;
            if (chatType === "forum" || chatType === "circle") {
                await chatService.deleteCommunityComment(Number(msg.id));
                // Local state cleanup if needed (WS should trigger update, but as a fallback)
                setMessages(prev => prev.filter(m => m.id !== msg.id));
            } else {
                await chatService.deleteMessage(Number(msg.id));
            }
        }
    };

    // 9. Operations: Load More Messages (Infinite Scroll)
    /**
     * Al abrir un chat, relee su detalle desde el servidor.
     *
     * La lista de conversaciones se carga UNA vez al montar la pagina, asi que el estado anidado de
     * cada hilo —para expertise, `expertiseRequestDetail`— queda congelado en ese momento. Si la
     * contraparte acepta o reagenda desde su sesion, aqui se seguia viendo lo viejo: la tarjeta
     * mostraba una propuesta ya resuelta y ofrecia acciones que el backend iba a rechazar. El panel
     * de Steps no lo sufria porque pide sus datos por separado, y de ahi que ambos se contradijeran.
     *
     * La dependencia es el uuid (un string) y no `selected`: usando el objeto, la propia
     * actualizacion volveria a disparar el efecto.
     */
    useEffect(() => {
        const uuid = selected?.uuid;
        if (!uuid) return;

        let alive = true;
        chatService.getConversation(uuid).then((fresh: any) => {
            if (!alive || !fresh) return;
            setSelected((prev: any) => {
                if (prev?.uuid !== fresh.uuid) return prev;
                // Solo se adopta si de verdad cambio: reemplazar el objeto sin necesidad reinicia
                // el efecto del chat, que recarga mensajes y reconecta el WebSocket.
                const before = JSON.stringify(prev.expertiseRequestDetail ?? null);
                const after = JSON.stringify(fresh.expertiseRequestDetail ?? null);
                return before === after ? prev : {...prev, ...fresh};
            });
            setConversations((prev: any[]) =>
                prev.map((c) => (c.uuid === fresh.uuid ? {...c, ...fresh} : c))
            );
        });

        return () => { alive = false };
    }, [selected?.uuid]);

    /**
     * Relee el hilo tras una accion de Expertise (aceptar, rechazar, reagendar, confirmar).
     *
     * Trae las DOS cosas a proposito: los mensajes, para que aparezca la tarjeta de sistema nueva,
     * y la conversacion, porque el estado de la solicitud —incluida la fecha propuesta— viaja
     * anidado en ella como `expertiseRequestDetail`, no en los mensajes. Recargar solo los
     * mensajes dejaba la tarjeta pintando una propuesta vacia.
     */
    const handleRefreshThread = useCallback(async () => {
        if (!selected?.uuid) return;

        const [conversation, response] = await Promise.all([
            chatService.getConversation(selected.uuid),
            chatService.getMessages(selected.uuid),
        ]);

        if (conversation) {
            setSelected((prev: any) => (prev?.uuid === conversation.uuid ? conversation : prev));
            setConversations((prev: any[]) =>
                prev.map((c) => (c.uuid === conversation.uuid ? {...c, ...conversation} : c))
            );
        }

        if (response?.results) {
            const mapped = await Promise.all(
                response.results.map((m: any) => mapMessage(m, sharedKey))
            );
            setMessages(mapped);
            setNextPage(response.next);
        }
    }, [selected, sharedKey, mapMessage]);

    const handleLoadMore = useCallback(async () => {
        if (!selected || !nextPage || loadingMore) return;
        setLoadingMore(true);
        try {
            const response = await chatService.getMessages(selected.uuid, nextPage);
            const mappedHistory = await Promise.all(response.results.map((m: any) => mapMessage(m, sharedKey)));
            setMessages(prev => [...prev, ...mappedHistory]);
            setNextPage(response.next);
        } catch (e) {
            console.error("Failed to load more messages:", e);
        } finally {
            setLoadingMore(false);
        }
    }, [selected, nextPage, loadingMore, sharedKey, mapMessage]);

    // 10. Operations: Leave Conversation
    const handleLeaveConversation = async () => {
        if (!selected) return;
        await chatService.leaveConversation(selected.uuid);
        setSelected(null);
        setShowModal(false);
        loadConversations();
    };

    return (
        <>
            {/* MODALS */}
            <MessageSimpleModal 
                show={showModal && modalAction === "leave"}
                title="Exit forum?"
                content="This is a private forum, if you exit you won't be able to join again"
                acceptContent="Exit forum" 
                onClose={(accepted) => {
                    if (accepted) {
                        handleLeaveConversation();
                    } else {
                        setShowModal(false);
                    }
                }}
            />

            {/* MESSAGES CONTENT */}
            <div className="messages-page-wrapper">
                <div 
                    className="messages-page-list" 
                    style={{ overflowY: 'auto' }}
                    onScroll={(e) => {
                        const target = e.currentTarget;
                        const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 50;
                        if (isAtBottom && hasMoreConversations && !loadingConversations) {
                            loadConversations(true);
                        }
                    }}
                >
                    <MessagesListHeader 
                        search={search}
                        onSearchChange={setSearch}
                        filter={filter}
                        onFilterChange={setFilter}
                    />
                    {filteredConversations.map((m, i) =>
                        <div key={m.uuid}>
                            <MessageItem
                                selected={selected?.uuid === m.uuid}
                                onSelect={() => handleSelectConversation(m)}
                                avatars={Array.isArray(m.avatar) ? m.avatar : (m.avatar ? [m.avatar] : [])}
                                host={m.isHost ?? m.is_host}
                                section={((m.chatType ?? m.chat_type) === "forum" ? "Community" : (m.chatType ?? m.chat_type) === "circle" ? "Mastermind Circles" : (m.chatType ?? m.chat_type) === "expertise" ? "Expertise" : (m.chatType ?? m.chat_type ?? "")).toUpperCase()}
                                previewContent={m.title}
                                lastMessage={m.lastMessage?.previewText || m.last_message?.preview_text || "No hay mensajes"}
                                lastMessageAuthor={m.lastMessage?.senderName || m.last_message?.sender_name || null}
                                createdAt={m.lastMessage?.stamptime || m.last_message?.stamptime || m.createdAt || m.created_at}
                                unreadCount={selected?.uuid === m.uuid ? 0 : (m.unreadCount ?? m.unread_count ?? 0)}
                            />
                            {i !== filteredConversations.length - 1 && 
                                <svg className="ms-auto me-auto" xmlns="http://www.w3.org/2000/svg" width="295" height="1" viewBox="0 0 295 1" fill="none">
                                    <path d="M0 0.5H295" stroke="#E9E9E9"/>
                                </svg>
                            }
                        </div> 
                    )}
                    {loadingConversations && (
                        <div className="text-center p-3 text-muted">Cargando chats...</div>
                    )}
                </div>
                <div className="messages-page-content">
                    {selected ? (
                        <>
                            {(selected.chatType === "forum" || selected.chat_type === "forum" || selected.chatType === "circle" || selected.chat_type === "circle") && (
                                <MastermindCirclesContent 
                                    conversation={selected}
                                    messages={messages}
                                    onSendMessage={handleSendMessage}
                                    onReact={handleReact}
                                    onLeave={() => {
                                        setModalAction("leave");
                                        setShowModal(true);
                                    }}
                                    onLoadMore={handleLoadMore}
                                    hasMore={!!nextPage}
                                    onDelete={handleDeleteMessage}
                                />
                            )}
                            {(selected.chatType === "experience" || selected.chat_type === "experience") && (
                                <ExperiencesContent 
                                    conversation={selected}
                                    messages={messages}
                                    onSendMessage={handleSendMessage}
                                    onReact={handleReact}
                                    onLeave={() => {
                                        setModalAction("leave");
                                        setShowModal(true);
                                    }}
                                    onLoadMore={handleLoadMore}
                                    hasMore={!!nextPage}
                                    onDelete={handleDeleteMessage}
                                />
                            )}
                            {(selected.chatType === "expertise" || selected.chat_type === "expertise") && (
                                <ExpertiseContent
                                    conversation={selected}
                                    messages={messages}
                                    onSendMessage={handleSendMessage}
                                    onReact={handleReact}
                                    onLeave={() => {
                                        setModalAction("leave");
                                        setShowModal(true);
                                    }}
                                    onLoadMore={handleLoadMore}
                                    hasMore={!!nextPage}
                                    onDelete={handleDeleteMessage}
                                    onRefresh={handleRefreshThread}
                                />
                            )}
                            {(selected.chatType === "introduction" || selected.chat_type === "introduction") && (
                                <IntroductionContent 
                                    conversation={selected}
                                    messages={messages}
                                    onSendMessage={handleSendMessage}
                                    onReact={handleReact}
                                    onLeave={() => {
                                        setModalAction("leave");
                                        setShowModal(true);
                                    }}
                                    onLoadMore={handleLoadMore}
                                    hasMore={!!nextPage}
                                    onDelete={handleDeleteMessage}
                                />
                            )}
                        </>
                    ) : (
                        <div className="d-flex align-items-center justify-content-center text-muted h-100 w-100 message-no-selected flex-column">
                            <div className="help-center-sections w-100 tap-flex-center justify-content-start mb-6">
                                <div className="nb-link-underline-500 w-auto cursor-pointer" onClick={() => history.goBack()}>
                                    Back
                                </div>
                            </div>
                            <div className="h-100 w-100">
                                Select a chat to start a conversation.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default MessagesPage;
